<template>
    <page-shell
        title="Opening hours"
        subtitle="Set the weekly hours. Close the location for a holiday or part of a day. Extra opening replaces the weekly hours on the dates you pick, so a normally closed Monday can open. Bookings that already exist stay on the calendar."
    >
        <div class="opening-hours-location">
            <span class="field-label">Location</span>
            <v-select
                v-model="location"
                :options="locations"
                :clearable="false"
                label="name"
                placeholder="Select a location"
            />
        </div>

        <p v-if="!locationsLoading && locations.length === 0">You have no locations.</p>
        <spinner v-if="loading" :loading="loading" text="Loading opening hours..." />

        <template v-if="location && !loading">
            <section>
                <h2>Weekly hours</h2>
                <p v-if="pastMidnight" class="opening-hours-note">
                    A closing time earlier than the opening time stays open past midnight.
                </p>
                <p v-if="extras.length" class="opening-hours-note">
                    Some days are stored more than once. Bookings use the oldest row until you remove the extras and save.
                </p>
                <div v-for="day in days" :key="day.day" class="day-row">
                    <span class="day-name">{{ day.day }}</span>
                    <el-checkbox v-model="day.open">Open</el-checkbox>
                    <date-picker v-model="day.from" type="time" format="HH:mm" value-type="HH:mm" :clearable="false" :minute-step="5" />
                    <date-picker v-model="day.till" type="time" format="HH:mm" value-type="HH:mm" :clearable="false" :minute-step="5" />
                    <span v-if="day.created" class="opening-hours-note">Not saved yet</span>
                </div>
                <div v-for="extra in extras" :key="extra.id" class="day-row day-row--extra">
                    <span class="day-name">{{ extra.day }}</span>
                    <span>{{ extra.open ? "Open" : "Closed" }}</span>
                    <span>{{ hhmmToTime(extra.from) }} – {{ hhmmToTime(extra.till) }}</span>
                    <el-button type="text" @click="removeExtra(extra.id)">Remove</el-button>
                </div>
                <el-button type="primary" :loading="savingWeek" :disabled="extras.length > 0" @click="saveWeek">
                    Save weekly hours
                </el-button>
            </section>

            <section>
                <h2>Extra opening</h2>
                <p class="opening-hours-note">
                    These dates replace the weekly hours. A closure on the same day still closes that time.
                    Use the same opening and closing time only by closing the day with a closure.
                </p>
                <el-table :data="overrides" empty-text="No extra opening">
                    <el-table-column label="From">
                        <template slot-scope="scope">{{ formatCalendarDate(scope.row.start_date) }}</template>
                    </el-table-column>
                    <el-table-column label="Until">
                        <template slot-scope="scope">{{ formatCalendarDate(scope.row.end_date) }}</template>
                    </el-table-column>
                    <el-table-column label="Hours">
                        <template slot-scope="scope">{{ hhmmToTime(scope.row.from) }} – {{ hhmmToTime(scope.row.till) }}</template>
                    </el-table-column>
                    <el-table-column label="" width="120">
                        <template slot-scope="scope">
                            <el-button type="text" @click="removeOverride(scope.row)">Remove</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <div class="add-form">
                    <date-picker
                        v-model="overrideDates"
                        type="date"
                        range
                        format="DD-MM-YYYY"
                        value-type="YYYY-MM-DD"
                        placeholder="From – until"
                    />
                    <date-picker v-model="overrideFrom" type="time" format="HH:mm" value-type="HH:mm" :clearable="false" :minute-step="5" />
                    <date-picker v-model="overrideTill" type="time" format="HH:mm" value-type="HH:mm" :clearable="false" :minute-step="5" />
                    <el-button type="primary" :loading="savingOverride" @click="addOverride">Add extra opening</el-button>
                </div>
            </section>

            <section>
                <h2>Temporarily closed</h2>
                <p class="opening-hours-note">
                    A whole day runs from midnight up to, but not including, the next midnight.
                    A closure only stops new bookings.
                </p>
                <el-table :data="closures" empty-text="No closure hours">
                    <el-table-column label="From">
                        <template slot-scope="scope">{{ formatInLocation(scope.row.start, location.time_zone) }}</template>
                    </el-table-column>
                    <el-table-column label="Until">
                        <template slot-scope="scope">{{ formatInLocation(scope.row.end, location.time_zone) }}</template>
                    </el-table-column>
                    <el-table-column label="" width="120">
                        <template slot-scope="scope">
                            <el-button type="text" @click="removeClosure(scope.row)">Remove</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <div class="add-form">
                    <el-radio-group v-model="closureMode">
                        <el-radio label="days">Whole days</el-radio>
                        <el-radio label="part">Part of a day</el-radio>
                    </el-radio-group>
                    <date-picker
                        v-if="closureMode === 'days'"
                        v-model="closureDates"
                        type="date"
                        range
                        format="DD-MM-YYYY"
                        value-type="YYYY-MM-DD"
                        placeholder="From – until"
                    />
                    <template v-else>
                        <date-picker v-model="closureDate" type="date" format="DD-MM-YYYY" value-type="YYYY-MM-DD" placeholder="Date" />
                        <date-picker v-model="closureFrom" type="time" format="HH:mm" value-type="HH:mm" :clearable="false" :minute-step="5" />
                        <date-picker v-model="closureTill" type="time" format="HH:mm" value-type="HH:mm" :clearable="false" :minute-step="5" />
                    </template>
                    <el-button type="primary" :loading="savingClosure" @click="addClosure">Add closure</el-button>
                </div>
            </section>
        </template>
    </page-shell>
</template>

<script setup>
import { Message, MessageBox } from "element-ui";
import { computed, onMounted, ref, watch } from "vue";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { getUserLocationsSimple } from "../../api/graphql/locations";
import {
    ValidationError,
    createClosure,
    createOpeningHourOverride,
    deleteClosure,
    deleteOpeningHourOverride,
    getClosures,
    getOpeningHourOverrides,
    getOpeningHours,
    replaceOpeningHours,
} from "../../api/rest";
import {
    buildWeekPayload,
    closureInterval,
    formatCalendarDate,
    formatInLocation,
    hhmmToTime,
    overridePayload,
    runsPastMidnight,
    timeToHhmm,
    weekDraftFromRows,
} from "../../helpers/openingHours";
import PageShell from "../shared/PageShell.vue";
import Spinner from "../shared/Spinner.vue";

const locations = ref([]);
const locationsLoading = ref(true);
const location = ref(null);
const loading = ref(false);
const days = ref([]);
const extras = ref([]);
const deleteIds = ref([]);
const closures = ref([]);
const overrides = ref([]);
const savingWeek = ref(false);
const savingClosure = ref(false);
const savingOverride = ref(false);
const closureMode = ref("days");
const closureDates = ref(null);
const closureDate = ref(null);
const closureFrom = ref("12:00");
const closureTill = ref("17:00");
const overrideDates = ref(null);
const overrideFrom = ref("10:00");
const overrideTill = ref("18:00");

const pastMidnight = computed(() =>
    days.value.some(
        (day) => day.open && runsPastMidnight(timeToHhmm(day.from), timeToHhmm(day.till)),
    ),
);

const fail = (error, fallback) => {
    Message.error(error instanceof ValidationError ? error.message : fallback);
};

const loadSchedule = async (locationId) => {
    loading.value = true;
    try {
        const [hours, closureRows, overrideRows] = await Promise.all([
            getOpeningHours(locationId),
            getClosures(locationId),
            getOpeningHourOverrides(locationId),
        ]);
        const draft = weekDraftFromRows(hours);
        days.value = draft.days;
        extras.value = draft.extras;
        deleteIds.value = [];
        closures.value = closureRows;
        overrides.value = overrideRows;
    } catch (error) {
        fail(error, "Failed to load opening hours");
    } finally {
        loading.value = false;
    }
};

const removeExtra = (id) => {
    extras.value = extras.value.filter((row) => row.id !== id);
    deleteIds.value = [...deleteIds.value, id];
};

const saveWeek = async () => {
    if (extras.value.length > 0) {
        Message.error("Remove the extra rows before saving");
        return;
    }
    const missingTime = days.value.some(
        (day) => timeToHhmm(day.from) == null || timeToHhmm(day.till) == null,
    );
    if (missingTime) {
        Message.error("Enter a time for every day");
        return;
    }
    const sameTime = days.value.some(
        (day) => day.open && timeToHhmm(day.from) === timeToHhmm(day.till),
    );
    if (sameTime) {
        Message.error("Opening and closing time must differ when the day is open");
        return;
    }
    savingWeek.value = true;
    try {
        const saved = await replaceOpeningHours(
            location.value.id,
            buildWeekPayload(days.value, deleteIds.value),
        );
        const draft = weekDraftFromRows(saved);
        days.value = draft.days;
        extras.value = draft.extras;
        deleteIds.value = [];
        Message.success("Opening hours saved");
    } catch (error) {
        fail(error, "Failed to save opening hours");
    } finally {
        savingWeek.value = false;
    }
};

const addClosure = async () => {
    const range =
        closureMode.value === "days" ? closureDates.value : [closureDate.value, closureDate.value];
    if (!range || !range[0] || !range[1]) {
        Message.error("Pick a date");
        return;
    }
    if (range[1] < range[0]) {
        Message.error("The end date is before the start date");
        return;
    }
    const interval = closureInterval({
        mode: closureMode.value,
        startDate: range[0],
        endDate: range[1],
        from: closureFrom.value,
        till: closureTill.value,
        timeZone: location.value.time_zone,
    });
    if (!interval) {
        Message.error("Opening and closing time must differ");
        return;
    }
    savingClosure.value = true;
    try {
        await createClosure(location.value.id, interval);
        closures.value = await getClosures(location.value.id);
        closureDates.value = null;
        closureDate.value = null;
        Message.success("Closure added");
    } catch (error) {
        fail(error, "Failed to add closure");
    } finally {
        savingClosure.value = false;
    }
};

const removeClosure = async (row) => {
    try {
        await MessageBox.confirm(
            "Remove this closure? Existing bookings stay. New bookings can use this time again.",
            "Remove closure",
            { confirmButtonText: "Remove", cancelButtonText: "Cancel", type: "warning" },
        );
    } catch {
        // Cancel leaves the closure in place.
        return;
    }
    try {
        await deleteClosure(location.value.id, row.id);
        closures.value = closures.value.filter((item) => item.id !== row.id);
        Message.success("Closure removed");
    } catch (error) {
        fail(error, "Failed to remove closure");
    }
};

const addOverride = async () => {
    if (!overrideDates.value || !overrideDates.value[0] || !overrideDates.value[1]) {
        Message.error("Pick a date");
        return;
    }
    if (overrideDates.value[1] < overrideDates.value[0]) {
        Message.error("The end date is before the start date");
        return;
    }
    const body = overridePayload({
        startDate: overrideDates.value[0],
        endDate: overrideDates.value[1],
        from: overrideFrom.value,
        till: overrideTill.value,
    });
    if (!body) {
        Message.error("Opening and closing time must differ");
        return;
    }
    savingOverride.value = true;
    try {
        await createOpeningHourOverride(location.value.id, body);
        overrides.value = await getOpeningHourOverrides(location.value.id);
        overrideDates.value = null;
        Message.success("Extra opening added");
    } catch (error) {
        fail(error, "Failed to add extra opening");
    } finally {
        savingOverride.value = false;
    }
};

const removeOverride = async (row) => {
    try {
        await MessageBox.confirm(
            "Remove this extra opening? The weekly hours apply again.",
            "Remove extra opening",
            { confirmButtonText: "Remove", cancelButtonText: "Cancel", type: "warning" },
        );
    } catch {
        // Cancel leaves the extra opening in place.
        return;
    }
    try {
        await deleteOpeningHourOverride(location.value.id, row.id);
        overrides.value = overrides.value.filter((item) => item.id !== row.id);
        Message.success("Extra opening removed");
    } catch (error) {
        fail(error, "Failed to remove extra opening");
    }
};

watch(location, (selected) => {
    if (selected?.id) {
        loadSchedule(selected.id);
    }
});

onMounted(async () => {
    try {
        const rows = await getUserLocationsSimple();
        locations.value = [...rows].sort((a, b) => a.name.localeCompare(b.name));
        location.value = locations.value[0] || null;
    } catch {
        Message.error("Failed to load locations");
    } finally {
        locationsLoading.value = false;
    }
});
</script>

<style scoped lang="scss">
@use "../../colors" as *;

.opening-hours-note {
    margin: 0 0 1rem;
    line-height: 1.45;
}

.opening-hours-location {
    max-width: 24rem;
    margin-bottom: 1.5rem;
}

.field-label {
    display: block;
    margin-bottom: 0.35rem;
}

section {
    margin-bottom: 2.5rem;
}

.day-row {
    display: grid;
    grid-template-columns: 8rem 6rem 10.5rem 10.5rem auto;
    gap: 0.75rem;
    align-items: center;
    margin-bottom: 0.5rem;
}

.day-row :deep(.mx-datepicker) {
    width: 100%;
}

.day-row--extra {
    opacity: 0.85;
}

.add-form {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    margin-top: 1rem;
}

.add-form :deep(.mx-datepicker) {
    width: 10rem;
}

.add-form :deep(.mx-datepicker-range) {
    width: 18rem;
}

@media (max-width: 768px) {
    .day-row {
        grid-template-columns: 1fr 1fr;
    }
}
</style>
